// /patients (IA §2.3) — فهرست جستجوی مراجعه‌کنندگان (همان قواعد SCR-STAFF-100 در صفحه) با «مراجعه‌کننده جدید».
// AP-PAT-02: پیوند به پرونده‌های ناقص (SCR-STAFF-107)، صف تکراری‌ها (SCR-STAFF-104) و ادغام (SCR-STAFF-105) بر اساس مجوز.
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { PatientSearchItem } from '@phoenix/api-client';
import { useAccess, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, ListPage, toJalali, type DataTableColumn } from '@phoenix/ui';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { DUPLICATES_PATH, INCOMPLETE_PATH, MERGE_PATH, PatientBadgeChips, patientPath, REQUESTS_PATH } from './common';
import { usePatientActions } from './PatientQuickActions';
import { usePatientSearch } from './PatientSearchPalette';

export const SCR_STAFF_PATIENTS = 'SCR-STAFF-100-LIST';

export function PatientsPage() {
  const { t } = useTranslation('pat');
  const navigate = useNavigate();
  const actions = usePatientActions();
  const write = useWriteAccess('pat.patient.create');
  const access = useAccess();
  const [text, setText] = useState('');
  const search = usePatientSearch(text, 50);

  const columns: DataTableColumn<PatientSearchItem>[] = [
    {
      id: 'name',
      headerKey: 'pat:page.column_name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.displayName}</span>
          <PatientBadgeChips badges={row.badges} />
        </Stack>
      ),
    },
    { id: 'file', headerKey: 'pat:profile.file_number', cell: (row) => <bdi dir="ltr">{faDigits(row.fileNumber)}</bdi> },
    {
      id: 'age',
      headerKey: 'pat:profile.age',
      cell: (row) => (row.age !== null && row.age !== undefined ? faDigits(row.age) : t('common:states.none')),
    },
    {
      id: 'mobile',
      headerKey: 'pat:fields.mobile',
      cell: (row) => (row.mobileHint ? t('pat:search.mobile_hint', { digits: faDigits(row.mobileHint) }) : t('common:states.none')),
    },
    {
      id: 'lastVisit',
      headerKey: 'pat:profile.last_visit',
      cell: (row) => (row.lastVisitAt ? toJalali(row.lastVisitAt) : t('pat:search.no_visit')),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_PATIENTS}
      titleKey="pat:page.title"
      subtitleKey="pat:page.subtitle"
      actions={
        <>
          {access.can('pat.patient.update') && (
            <Button component={RouterLink} to={INCOMPLETE_PATH} data-testid="patients-incomplete">
              {t('pat:temporary.queue')}
            </Button>
          )}
          {access.can('pat.duplicates.review') && (
            <Button component={RouterLink} to={DUPLICATES_PATH} data-testid="patients-duplicates">
              {t('pat:duplicates_queue.title')}
            </Button>
          )}
          {(access.can('pat.patient.update') || access.can('pat.patient.export') || access.can('pat.patient.update_identity')) && (
            <Button component={RouterLink} to={REQUESTS_PATH} data-testid="patients-requests">
              {t('pat:requests.title')}
            </Button>
          )}
          {access.can('pat.patient.merge') && (
            <Button component={RouterLink} to={MERGE_PATH} data-testid="patients-merge">
              {t('pat:merge.title')}
            </Button>
          )}
          {write.canWrite && actions ? (
            <Button
              variant="contained"
              startIcon={<PersonAddAltOutlinedIcon />}
              onClick={() => actions.openRegister(text)}
              data-testid="patients-new"
            >
              {t('pat:quick.new_patient')}
            </Button>
          ) : null}
        </>
      }
      filters={
        <TextField
          fullWidth
          autoFocus
          value={text}
          onChange={(event) => setText(event.target.value)}
          label={t('pat:search.label')}
          slotProps={{
            htmlInput: { 'data-testid': 'patients-search', autoComplete: 'off' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      }
    >
      <DataTable
        captionKey="pat:search.results"
        columns={columns}
        rows={text.trim() ? search.data?.items : []}
        getRowId={(row) => row.id}
        isLoading={text.trim().length > 0 && search.isPending}
        error={search.error}
        onRetry={() => void search.refetch()}
        onRowClick={(row) => void navigate(patientPath(row.id))}
        emptyTitleKey={text.trim() ? 'pat:search.empty' : 'pat:page.start_typing'}
      />
    </ListPage>
  );
}
